import type {
  CrafterMultiWishlistQuery,
  WishlistItem
} from '@/__generated__/gateway/graphql';
import type { MoneyV2 } from '@/__generated__/storefront/graphql';

// Type for a single wishlist from multi-wishlist query
export type Wishlist = NonNullable<
  NonNullable<
    NonNullable<CrafterMultiWishlistQuery['crafter']>['wishlists']
  >['wishlists']
>[number];

// Extended wishlist item type that includes the wishlistId
export type WishlistItemWithWishlistId = WishlistItem & {
  wishlistId: string;
};

export type ProductData = {
  title: string;
  content: string;
  size: string | null;
  price: MoneyV2 | null;
  compareAtPrice: MoneyV2 | null;
  handle: string | null;
  variantId: string | null; // Numeric variant ID for SKU-based items
  availableForSale: boolean | null; // Product or variant availability
  quantityAvailable: number | null; // Variant quantity available
  isPattern?: boolean; // True if this is a downloadable pattern (digital product), optional for backward compatibility with cached data
  isGiftCard?: boolean; // True if this is a gift card product, optional for backward compatibility with cached data
};

// ProductNode type for Storefront API results (used in product data fetching)
export type ProductNode = {
  __typename?: string;
  id?: string;
  handle?: string;
  title?: string;
  availableForSale?: boolean;
  isGiftCard?: boolean;
  priceRange?: {
    minVariantPrice?: MoneyV2;
    maxVariantPrice?: MoneyV2;
  } | null;
  compareAtPriceRange?: {
    minVariantPrice?: MoneyV2;
    maxVariantPrice?: MoneyV2;
  } | null;
  featuredImage?: { url?: string } | null;
  productGroupIdMetafield?: {
    value?: string;
  } | null;
  metafields?: Array<{
    key?: string;
    value?: string;
  } | null>;
  variants?: {
    nodes?: Array<{
      id?: string;
      sku?: string;
      title?: string;
      availableForSale?: boolean;
      quantityAvailable?: number;
      price?: MoneyV2;
      compareAtPrice?: MoneyV2;
      image?: { url?: string } | null;
    }>;
  } | null;
};

// Type for wishlist search results from Storefront API
export type WishlistSearchResult = {
  products?: { nodes?: unknown[] } | null;
  patterns?: { nodes?: unknown[] } | null;
};

// Type for query result structure
export type ProductResult = {
  data?: {
    products?: { nodes?: unknown[] } | null;
  };
};

// Type for items used in product data fetching
export type WishlistItemLike = {
  id: string;
  product_group_identifier?: string | null;
  sku?: string | null;
};

// Constants for localStorage cache
export const STORAGE_KEY_IMAGES = 'wishlist_images_cache';
export const STORAGE_KEY_PRODUCT_DATA = 'wishlist_product_data_cache';
export const STORAGE_KEY_ITEMS = 'wishlist_items_cache';
export const STORAGE_KEY_WISHLISTS = 'wishlists_cache';
export const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// Constants for thumbnail dimensions
export const THUMBNAIL_WIDTH = 160;
export const THUMBNAIL_HEIGHT = 160;

// Constants for concurrent fetch prevention
export const CONCURRENT_FETCH_TIMEOUT_MS = 10000; // 10 seconds

// Constants for pagination
export const WISHLISTS_PAGE_SIZE = 5;

// Constants for wishlist name and description validation
export const WISHLIST_NAME_CHAR_LIMIT = 100;
export const WISHLIST_DESCRIPTION_CHAR_LIMIT = 240;
